import {
  createAction,
  createReducer,
} from '@reduxjs/toolkit'
import { setTag } from '@sentry/nextjs'
import mixpanel from 'mixpanel-browser'

import { FeatureKey } from '@/constants/feature-key'
import {
  FAVORITE_EMOJIS,
  FEATURE_KEYS,
} from '@/constants/local-storages'
import { startAppListening } from '@/redux/utils'

export interface AppInfo {
  version: string
}

const namespace = 'global'

export interface FanProfileModalInfo {
  userId?: number
  selector?: string
}

export type DisplayMode = 'light' | 'dark'

export const setFeatureKeys = createAction<FeatureKey[]>(`${namespace}/setFeatureKey`)
export const setIsLogin = createAction<boolean>(`${namespace}/setIsLogin`)
export const setInitializing = createAction<boolean>(`${namespace}/setInitializing`)
export const setFavoriteEmojis = createAction<string[]>(`${namespace}/setFavoriteEmojis`)
export const setAILoading = createAction<boolean>(`${namespace}/setAILoading`)
export const setDisplayMode = createAction<DisplayMode>(`${namespace}/setDisplayMode`)

export const defaultFeatureKeys = [FeatureKey.Home]

const reducer = createReducer<{
  featureKeys: FeatureKey[]
  permissionKey: FeatureKey
  isLogin: boolean
  initializing: boolean
  appInfo: AppInfo
  favoriteEmojis: string[]
  isAILoading: boolean
  fanProfileModalInfo: FanProfileModalInfo
  displayMode: DisplayMode,
}>({
  featureKeys: defaultFeatureKeys,
  permissionKey: defaultFeatureKeys[defaultFeatureKeys.length - 1],
  initializing: true,
  isLogin: false,
  appInfo: {
    version: '',
  },
  favoriteEmojis: ['🎉', '💙', '🥰', '😍', '👀', '🙈', '🍒', '🍑', '🍆', '💦', '😈', '🥵', '🌶️', '🔥', '😉', '😏', '😋', '😮', '😓', '😂', '😅'],
  isAILoading: false,
  fanProfileModalInfo: {},
  displayMode: 'light',
}, (builder) => {
  builder.addCase(setFeatureKeys, (draft, { payload }) => {
    draft.featureKeys = payload
    draft.permissionKey = payload[payload.length - 1]
  })
  builder.addCase(setIsLogin, (draft, { payload }) => {
    draft.isLogin = payload
    if (!payload) {
      draft.featureKeys = defaultFeatureKeys
    }
  })
  builder.addCase(setInitializing, (draft, { payload }) => {
    draft.initializing = payload
  })
  builder.addCase(setFavoriteEmojis, (draft, { payload }) => {
    draft.favoriteEmojis = payload
  })
  builder.addCase(setAILoading, (draft, { payload }) => {
    draft.isAILoading = payload
  })
  builder.addCase(setDisplayMode, (draft, { payload }) => {
    draft.displayMode = payload
  })
})

startAppListening({
  actionCreator: setFeatureKeys,
  effect: (_, api) => {
    try {
      const { featureKeys } = api.getState().global
      const currentKeysString = JSON.stringify(featureKeys)
      window.localStorage.setItem(FEATURE_KEYS, JSON.stringify(currentKeysString))
      setTag('featureKeys', currentKeysString)
      if (currentKeysString !== JSON.stringify(api.getOriginalState().global.featureKeys)) {
        mixpanel.track_pageview({
          page: featureKeys,
        })
      }
    } catch (e) {
      console.error(e)
    }
  },
})

startAppListening({
  actionCreator: setFavoriteEmojis,
  effect: (_, api) => {
    try {
      window.localStorage.setItem(
        FAVORITE_EMOJIS,
        JSON.stringify(api.getState().global.favoriteEmojis),
      )
    } catch (e) {
      console.error(e)
    }
  },
})

startAppListening({
  actionCreator: setIsLogin,
  effect: ({ payload }) => {
    if (!payload) {
      mixpanel.reset()
    }
  },
})

export default { [namespace]: reducer }
